import { useMemo } from 'react';

import { ContactChannel, IContact } from 'entities/Team/types';

import { useContactLink, type ContactLinkResult } from './useContactLink';

export type ContactsByChannel = {
  phone?: IContact;
  email?: IContact;
  telegram?: IContact;
  whatsapp?: IContact;
};

export type ContactLinks = {
  phone?: ContactLinkResult | null;
  email?: ContactLinkResult | null;
  telegram?: ContactLinkResult | null;
  whatsapp?: ContactLinkResult | null;
};

export type ContactTexts = {
  phone: string;
  email: string;
};

export type UseContactsResult = {
  contactsByChannel: ContactsByChannel;
  links: ContactLinks;
  texts: ContactTexts;
  hasMessengers: boolean;
  hasDirectContacts: boolean;
};

/**
 * Хук для работы с контактами
 * Группирует контакты по каналам, получает ссылки и форматирует тексты
 */
export const useContacts = (contacts: IContact[]): UseContactsResult => {
  const getContactLink = useContactLink();

  const contactsByChannel = useMemo(() => {
    const firstByChannel = contacts.reduce<Partial<Record<ContactChannel, IContact>>>((acc, c) => {
      if (!acc[c.contactChannel]) {
        acc[c.contactChannel] = c;
      }

      return acc;
    }, {});

    return {
      phone: firstByChannel[ContactChannel.phone],
      email: firstByChannel[ContactChannel.email],
      telegram: firstByChannel[ContactChannel.telegram],
      whatsapp: firstByChannel[ContactChannel.whatsapp],
    };
  }, [contacts]);

  const links = useMemo(() => {
    return {
      phone: getContactLink(contactsByChannel.phone),
      email: getContactLink(contactsByChannel.email),
      telegram: getContactLink(contactsByChannel.telegram),
      whatsapp: getContactLink(contactsByChannel.whatsapp),
    };
  }, [contactsByChannel, getContactLink]);

  const texts = useMemo(() => {
    const phoneText = contactsByChannel.phone ? (links.phone?.displayValue ?? contactsByChannel.phone.contact) : '';
    const emailText = contactsByChannel.email?.contact ?? '';

    return {
      phone: phoneText,
      email: emailText,
    };
  }, [contactsByChannel, links]);

  const hasMessengers = Boolean(contactsByChannel.telegram || contactsByChannel.whatsapp);
  const hasDirectContacts = Boolean(contactsByChannel.phone || contactsByChannel.email);

  return {
    contactsByChannel,
    links,
    texts,
    hasMessengers,
    hasDirectContacts,
  };
};
